import React, { useId } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Drawer, IconButton, Link, Toolbar, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../auth/useAuth';
import { dueLabel } from '../../dashboard/tasks/deriveTasks';
import { clientHref } from '../../dashboard/tasks/links';
import { CoachWorkload, plural, taskInColumn, WORKLOAD_COLUMNS, WorkloadColumn } from '../../dashboard/tasks/workload';
import { DueTag, INK } from '../../dashboard/ui';

/** The clients behind one cell of the workload table, each a step from being dealt with. */
export const WorkloadDrawer: React.FC<{
  coach: CoachWorkload | null;
  column: WorkloadColumn | null;
  onClose: () => void;
}> = ({ coach, column, onClose }) => {
  const open = Boolean(coach && column);
  // Only the new dashboard opens a client from the URL; the original one ignores it.
  const linksToActions = useAuth().hasFeature('new_dashboard');
  const headingId = useId();
  const definition = WORKLOAD_COLUMNS.find((c) => c.key === column);
  const label = definition?.label ?? '';
  const now = new Date();
  const items =
    coach && column
      ? coach.work
          .map((item) => ({ ...item, tasks: item.tasks.filter((task) => taskInColumn(task, column)) }))
          .filter((item) => item.tasks.length > 0)
      : [];
  const count = items.reduce((sum, item) => sum + item.tasks.length, 0);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': headingId, sx: { width: { xs: '100%', sm: 460 } } } as object}>
      {coach && column ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* The app bar sits above drawers; this spacer, sized like it, keeps the header and close button in view. */}
          <Toolbar aria-hidden sx={{ flexShrink: 0 }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2.5, borderBottom: `1px solid ${INK.faint}` }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, color: INK.muted }}>
                {coach.coachName} · {coach.location}
              </Typography>
              <Typography id={headingId} component="h2" sx={{ fontSize: 20, fontWeight: 600, mt: 0.25 }}>
                {label} · {plural(items.length, ['client', 'clients'])}
              </Typography>
              <Typography sx={{ fontSize: 14, color: INK.body, mt: 0.5 }}>
                {plural(items.length, ['client', 'clients'])} with {definition ? plural(count, definition.task) : count}
                {column === 'overdue' || column === 'session' ? '' : ' due'}, most urgent first.
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, px: 2.5, py: 0, overflowY: 'auto', flexGrow: 1 }}>
            {items.map((item) => (
              <Box component="li" key={item.client.clientId} sx={{ py: 1.75, '& + &': { borderTop: `1px solid ${INK.faint}` } }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Link component={RouterLink} to={clientHref(item.client)} underline="hover" sx={{ fontWeight: 600, color: INK.main }}>
                    {item.client.name}
                  </Link>
                  <Typography component="span" sx={{ fontSize: 12, color: INK.muted }}>
                    {item.client.programmeLabel}
                    {item.client.week ? ` · week ${item.client.week}` : ''}
                  </Typography>
                </Box>
                {item.tasks.map((task) => (
                  <Box key={task.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.5 }}>
                    <Typography sx={{ flexGrow: 1, fontSize: 13, color: INK.body }}>
                      {task.title}
                      {task.detail ? <Box component="span" sx={{ color: INK.muted }}> - {task.detail}</Box> : null}
                    </Typography>
                    <DueTag urgency={task.urgency}>{dueLabel(task, now)}</DueTag>
                  </Box>
                ))}
                {linksToActions ? (
                  <Link
                    component={RouterLink}
                    to={`/dashboard?view=inbox&client=${encodeURIComponent(item.client.clientId)}`}
                    underline="hover"
                    sx={{ display: 'inline-block', mt: 0.75, fontSize: 13, fontWeight: 600 }}
                  >
                    Open in Actions →
                  </Link>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Drawer>
  );
};
